import React, { useState, useEffect } from 'react';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import api from '../../api';
import { ptsFor } from '../../utils/points';

const RANK_SUFFIX = (n) => (n === 1 ? 'ST' : n === 2 ? 'ND' : n === 3 ? 'RD' : 'TH');

// Your points/chores/avg-grade for the last 7 days — treated as identity-level
// info on the Profile tab, not buried in the Analytics tab's full range picker.
const YourStandingCard = ({ currentUserId }) => {
    const [stats, setStats] = useState({ points: 0, count: 0, avgGrade: null, rank: null, totalPeople: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const from = startOfDay(subDays(new Date(), 7));
                const to = endOfDay(new Date());
                const res = await api.get(`/chores/logs/analytics?from=${from.toISOString()}&to=${to.toISOString()}`);
                if (cancelled) return;
                const logs = res.data;

                const byPerson = new Map();
                logs.forEach(log => {
                    (log.user || []).forEach(u => {
                        if (!byPerson.has(u.id)) byPerson.set(u.id, { points: 0, count: 0 });
                        const entry = byPerson.get(u.id);
                        entry.points += ptsFor(log.actualMinutes, log.actualDifficulty);
                        entry.count += 1;
                    });
                });

                const mine = byPerson.get(currentUserId) || { points: 0, count: 0 };
                const ranked = Array.from(byPerson.entries()).sort((a, b) => b[1].points - a[1].points);
                const rank = ranked.findIndex(([id]) => id === currentUserId) + 1;

                const myGrades = logs
                    .filter(l => (l.user || []).some(u => u.id === currentUserId))
                    .flatMap(l => l.othersUsersGrade || []);
                const avgGrade = myGrades.length > 0
                    ? (myGrades.reduce((s, g) => s + g.grade, 0) / myGrades.length).toFixed(1)
                    : null;

                setStats({ points: mine.points, count: mine.count, avgGrade, rank: rank || null, totalPeople: byPerson.size });
            } catch (err) {
                console.error('Error fetching your standing', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUserId]);

    if (loading) return <div className="h-[86px] rounded-2xl skeleton-shimmer mb-6" />;

    return (
        <div className="p-4 bg-ink-card rounded-2xl shadow-sm border border-white/10 mb-6">
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-medium text-ink-fg/40 uppercase tracking-widest">Your Standing · 7D</span>
                {stats.rank && stats.totalPeople > 1 && (
                    <span className="text-[10px] font-mono font-bold text-mint">{stats.rank}{RANK_SUFFIX(stats.rank)} OF {stats.totalPeople}</span>
                )}
            </div>
            <div className="flex items-end gap-6 mt-3">
                <div>
                    <div className="text-2xl font-serif text-ink-fg">{stats.points}</div>
                    <div className="text-[9.5px] font-mono text-ink-fg/40 mt-1">POINTS</div>
                </div>
                <div>
                    <div className="text-2xl font-serif text-ink-fg">{stats.count}</div>
                    <div className="text-[9.5px] font-mono text-ink-fg/40 mt-1">CHORES</div>
                </div>
                <div>
                    <div className="text-2xl font-serif text-ink-fg">{stats.avgGrade ?? '—'}</div>
                    <div className="text-[9.5px] font-mono text-ink-fg/40 mt-1">AVG GRADE</div>
                </div>
            </div>
        </div>
    );
};

export default YourStandingCard;
